import React, { useEffect, useState } from 'react';
import './App.css';
import EvaluationForm from './components/EvaluationForm';
import ResultsDisplay from './components/ResultsDisplay';
import FeedbackForm from './components/FeedbackForm';
import CookieBanner from './components/CookieBanner';
import api from './api/client';
import { initAnalytics, trackPageView, trackCalculationSubmitted } from './utils/analytics';

const calculatorHighlights = [
  {
    title: 'Start with cash flow, not emotion',
    description: 'The calculator compares your income, recurring obligations, debt load, and emergency savings before judging whether a purchase fits your budget.'
  },
  {
    title: 'Stress-test financed purchases',
    description: 'If you plan to borrow, the analysis factors in APR, down payment, term length, and how much the payment would consume from your disposable income.'
  },
  {
    title: 'Look beyond the sticker price',
    description: 'The score is designed to surface the trade-offs that often get missed: lower savings after the purchase, upcoming major expenses, and total interest cost.'
  }
];

const planningChecklist = [
  'Use monthly take-home income rather than gross pay so the decision reflects money you can actually spend.',
  'Include rent, utilities, insurance, subscriptions, and debt payments before you estimate what is left for a new purchase.',
  'Check whether the purchase would reduce your emergency fund below your comfort level.',
  'Compare the calculator result with your own goals, timeline, and risk tolerance before committing.'
];

const faqItems = [
  {
    question: 'What does the affordability score represent?',
    answer: 'The score is an educational estimate from 0 to 100. Higher scores mean the purchase appears easier to absorb based on your income, debt, savings cushion, and financing terms.'
  },
  {
    question: 'Can this replace financial advice?',
    answer: 'No. The tool is intended to organize your thinking, not replace a financial planner, lender, or accountant who can review your full situation.'
  },
  {
    question: 'Why does the tool ask about savings and upcoming expenses?',
    answer: 'A purchase can look affordable on a monthly basis and still leave you exposed if it drains cash reserves right before a move, medical bill, or other major obligation.'
  },
  {
    question: 'How should I use a risky or not-advised result?',
    answer: 'Treat it as a prompt to revisit the purchase amount, improve the down payment, shorten the term, or wait until your savings and debt profile are stronger.'
  }
];

function App() {
  const [currentPage, setCurrentPage] = useState('form'); // 'form' | 'results' | 'feedback'
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const price = params.get('price');
    const category = params.get('category');
    const type = params.get('type');
    const description = params.get('description');
    if (price || category || type || description) {
      const vals = {};
      if (price) vals.purchase_price = price;
      if (category) vals.purchase_category = category;
      if (type) vals.payment_type = type;
      if (description) vals.purchase_description = description;
      setInitialValues(vals);
    }
  }, []);

  useEffect(() => {
    trackPageView(`app_${currentPage}`);
  }, [currentPage]);

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new CustomEvent('cookie-consent:open'));
  };

  const handleEvaluate = async (formData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/evaluate', formData);
      setEvaluationResult(response.data);
      setCurrentPage('results');
      trackCalculationSubmitted({ paymentType: formData.payment_type, purchaseCategory: formData.purchase_category });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to evaluate affordability');
      console.error('Evaluation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (feedback) => {
    setLoading(true);
    setError(null);
    try {
      await api.post('/feedback', {
        evaluation_id: evaluationResult.evaluation_id,
        ...feedback
      });
      setCurrentPage('form');
      setEvaluationResult(null);
      alert('Thank you for your feedback!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback');
      console.error('Feedback error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewEvaluation = () => {
    setCurrentPage('form');
    setEvaluationResult(null);
    setError(null);
  };

  const handleEmailCapture = async (email) => {
    await api.post('/email-capture', {
      email,
      score: evaluationResult?.affordability_score,
      verdict: evaluationResult?.verdict
    });
  };

  const handleProvideFeedback = () => {
    setCurrentPage('feedback');
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Affordability Analyzer</h1>
            <p>Check it before you charge it</p>
            <div className="header-links">
              <a href="/about.html" target="_blank" rel="noopener noreferrer">About</a>
              <a href="/contact.html" target="_blank" rel="noopener noreferrer">Contact</a>
              <a href="#how-it-works">How it works</a>
              <a href="#faq">FAQ</a>
              <a href="/how-it-works.html" target="_blank" rel="noopener noreferrer">Full guide</a>
            </div>
          </div>
          {currentPage !== 'form' && (
            <button className="home-button" onClick={handleNewEvaluation}>
              ← Home
            </button>
          )}
        </div>
      </header>

      <main className="container">
        {error && <div className="alert alert-error">{error}</div>}

        {currentPage === 'form' && (
          <>
            <section className="content-card card intro-card">
              <span className="eyebrow">Decision support for real purchases</span>
              <h2>Use the calculator to pressure-test a purchase before it becomes a payment.</h2>
              <p>
                Affordability Analyzer is built for people comparing a purchase against the rest of their financial life.
                Instead of showing a payment estimate in isolation, it checks whether the purchase still looks reasonable after
                accounting for existing bills, debt obligations, cash reserves, and financing costs.
              </p>
              <div className="highlight-grid">
                {calculatorHighlights.map((item) => (
                  <article key={item.title} className="highlight-item">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <EvaluationForm
              onSubmit={handleEvaluate}
              loading={loading}
              initialValues={initialValues}
            />

            <section id="how-it-works" className="content-card card">
              <h2>How the analysis works</h2>
              <p>
                The model starts with your monthly take-home income and subtracts fixed expenses and debt payments to estimate
                disposable income. For financed purchases, it calculates the expected payment and total interest, then checks how
                much of your remaining monthly cash flow would be tied up. It also looks at what happens to your savings after a
                down payment so the score reflects both monthly pressure and balance-sheet risk.
              </p>
              <ol className="content-list ordered-list">
                <li>Enter the purchase amount and whether you plan to pay cash, finance it, or use a mixed structure.</li>
                <li>Add core financial inputs such as income, savings, fixed expenses, and any debt payments you already carry.</li>
                <li>Review the score, verdict, recommended purchase range, and key takeaways before making a decision.</li>
              </ol>
            </section>

            <section className="content-card card">
              <h2>Before you rely on a result</h2>
              <ul className="content-list">
                {planningChecklist.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="content-note">
                For a deeper explanation of the scoring logic and practical interpretation tips, use the full guides linked below.
              </p>
            </section>

            <section id="faq" className="content-card card faq-card">
              <h2>Frequently asked questions</h2>
              <div className="faq-grid">
                {faqItems.map((item) => (
                  <article key={item.question} className="faq-item">
                    <h3>{item.question}</h3>
                    <p>{item.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}

        {currentPage === 'results' && evaluationResult && (
          <ResultsDisplay
            result={evaluationResult}
            onNewEvaluation={handleNewEvaluation}
            onProvideFeedback={handleProvideFeedback}
            onEmailCapture={handleEmailCapture}
          />
        )}

        {currentPage === 'feedback' && evaluationResult && (
          <FeedbackForm
            onSubmit={handleSubmitFeedback}
            onCancel={() => setCurrentPage('results')}
            loading={loading}
          />
        )}
      </main>

      <footer className="app-footer">
        <p>⚠️ Educational estimate only. Not financial advice. Consult a financial advisor.</p>
        <div className="footer-links">
          <a href="/about.html" target="_blank" rel="noopener noreferrer">About</a>
          <span className="separator">•</span>
          <a href="/contact.html" target="_blank" rel="noopener noreferrer">Contact</a>
          <span className="separator">•</span>
          <a href="/how-it-works.html" target="_blank" rel="noopener noreferrer">How It Works</a>
          <span className="separator">•</span>
          <a href="/methodology.html" target="_blank" rel="noopener noreferrer">Methodology</a>
          <span className="separator">•</span>
          <a href="/cookie-policy.html" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
          <span className="separator">•</span>
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span className="separator">•</span>
          <a href="/disclaimer.html" target="_blank" rel="noopener noreferrer">Disclaimer</a>
          <span className="separator">•</span>
          <button type="button" className="footer-link-button" onClick={handleOpenCookieSettings}>Cookie Settings</button>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}

export default App;
